import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight } from "lucide-react";

export default function RequestProjectPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    project_idea: '',
    budget_range: '',
    location: ''
  });
  const [generatedProject, setGeneratedProject] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_idea.trim()) {
      toast.error("कृपया प्रोजेक्ट आइडिया दर्ज करें");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/projects/generate-custom`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedProject(response.data);
      toast.success("प्रोजेक्ट सफलतापूर्वक बनाया गया!");
    } catch (error) {
      toast.error("प्रोजेक्ट बनाने में त्रुटि");
    }
    setLoading(false);
  };

  const formatCost = (cost) => {
    return new Intl.NumberFormat('hi-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(cost);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h1 className="font-rajdhani font-bold text-2xl text-gray-800">
              नया प्रोजेक्ट बनाएं
            </h1>
            <p className="text-sm text-gray-8000">AI आपके लिए कस्टम प्रोजेक्ट बनाएगा</p>
          </div>
        </div>

        {!generatedProject ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="space-y-6">
              <div>
                <Label className="text-gray-600 text-base">
                  आपका बिजनेस आइडिया <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  placeholder="उदाहरण: मैं गांव में आचार बनाने का बिजनेस शुरू करना चाहता हूं..."
                  value={formData.project_idea}
                  onChange={(e) => setFormData({ ...formData, project_idea: e.target.value })}
                  className="input-industrial mt-2 min-h-[120px]"
                  required
                  data-testid="idea-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">बजट सीमा (वैकल्पिक)</Label>
                  <Input
                    placeholder="उदा: 5-10 लाख"
                    value={formData.budget_range}
                    onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                    className="input-industrial mt-1"
                    data-testid="budget-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">स्थान (वैकल्पिक)</Label>
                  <Input
                    placeholder="उदा: जयपुर, राजस्थान"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="input-industrial mt-1"
                    data-testid="location-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary rounded-sm text-lg py-6"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    AI प्रोजेक्ट बना रहा है...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    प्रोजेक्ट Generate करें
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 text-emerald-500 mb-4">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">प्रोजेक्ट सफलतापूर्वक बनाया गया!</span>
            </div>

            <h2 className="font-rajdhani font-bold text-2xl text-gray-800 mb-2">
              {generatedProject.name_hindi}
            </h2>
            <p className="text-gray-8000 mb-4">{generatedProject.name}</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-8000 mb-1">श्रेणी</p>
                <p className="text-gray-700 font-medium">{generatedProject.category}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-8000 mb-1">अनुमानित लागत</p>
                <p className="text-orange-500 font-bold font-tabular-nums">
                  {formatCost(generatedProject.total_cost)}
                </p>
              </div>
            </div>

            <p className="text-gray-500 mb-6">
              {generatedProject.description_hindi || generatedProject.description}
            </p>

            <div className="flex gap-4">
              <Button
                onClick={() => navigate(`/project/${generatedProject.id}`)}
                className="btn-primary rounded-sm flex-1"
                data-testid="view-project-btn"
              >
                विस्तृत रिपोर्ट देखें
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => setGeneratedProject(null)}
                variant="outline"
                className="btn-secondary rounded-sm"
              >
                नया बनाएं
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
